import React from "react";
import {
  ChevronRight,
  Home,
  Settings,
  Calendar,
  List,
  User,
} from "lucide-react";

interface BreadcrumbItem {
  label: string;
  href?: string;
  icon?: React.ReactNode;
  active?: boolean;
}

interface NavigationFlowProps {
  breadcrumbs: BreadcrumbItem[];
  nextSteps?: {
    title: string;
    description: string;
    action?: () => void;
    actionLabel?: string;
  }[];
  currentPage: string;
  onNavigate?: (page: string) => void;
}

export const NavigationFlow: React.FC<NavigationFlowProps> = ({
  breadcrumbs,
  nextSteps,
  currentPage,
  onNavigate,
}) => {
  return (
    <div className="space-y-4">
      {/* Breadcrumb Navigation */}
      <nav className="flex items-center space-x-2 text-sm text-gray-600 bg-white/60 backdrop-blur-sm rounded-lg px-4 py-2">
        {breadcrumbs.map((item, index) => (
          <React.Fragment key={index}>
            {index > 0 && <ChevronRight size={14} className="text-gray-400" />}
            <div
              className={`flex items-center gap-1 ${
                item.active
                  ? "text-blue-600 font-medium"
                  : item.href
                  ? "hover:text-blue-600 cursor-pointer"
                  : "text-gray-500"
              }`}
              onClick={
                item.href && onNavigate
                  ? () => onNavigate(item.href!)
                  : undefined
              }
            >
              {item.icon && <span className="w-4 h-4">{item.icon}</span>}
              <span>{item.label}</span>
            </div>
          </React.Fragment>
        ))}
      </nav>

      {/* Next Steps Suggestions */}
      {nextSteps && nextSteps.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h3 className="text-sm font-medium text-blue-900 mb-2">
            💡 What's next?
          </h3>
          <div className="space-y-2">
            {nextSteps.map((step, index) => (
              <div key={index} className="flex items-start gap-3">
                <div className="w-5 h-5 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 text-xs font-medium mt-0.5">
                  {index + 1}
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-blue-900">
                    {step.title}
                  </p>
                  <p className="text-xs text-blue-700 mt-1">
                    {step.description}
                  </p>
                  {step.action && step.actionLabel && (
                    <button
                      onClick={step.action}
                      className="mt-2 text-xs bg-blue-600 text-white px-3 py-1 rounded-md hover:bg-blue-700 transition-colors"
                    >
                      {step.actionLabel}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Helper function to generate breadcrumbs based on current route
export const getBreadcrumbs = (currentPage: string): BreadcrumbItem[] => {
  const breadcrumbs: BreadcrumbItem[] = [
    { label: "Home", href: "dashboard", icon: <Home size={14} /> },
  ];

  switch (currentPage) {
    case "schedule":
      breadcrumbs.push({
        label: "Schedule Builder",
        href: "schedule",
        icon: <Calendar size={14} />,
        active: true,
      });
      break;
    case "presets":
      breadcrumbs.push({
        label: "Supplements",
        href: "presets",
        icon: <List size={14} />,
        active: true,
      });
      break;
    case "groups":
      breadcrumbs.push({
        label: "Supplements",
        href: "presets",
        icon: <List size={14} />,
      });
      breadcrumbs.push({
        label: "Groups",
        href: "groups",
        icon: <List size={14} />,
        active: true,
      });
      break;
    case "settings":
      breadcrumbs.push({
        label: "Settings",
        href: "settings",
        icon: <Settings size={14} />,
        active: true,
      });
      break;
    case "history":
      breadcrumbs.push({
        label: "History",
        href: "history",
        icon: <User size={14} />,
        active: true,
      });
      break;
    default:
      breadcrumbs[0].active = true;
  }

  return breadcrumbs;
};

// Helper function to generate next steps suggestions
export const getNextSteps = (
  currentPage: string,
  hasSchedule: boolean,
  hasPlan: boolean,
  supplementCount: number,
  onNavigate?: (page: string) => void
) => {
  const nextSteps: {
    title: string;
    description: string;
    action?: () => void;
    actionLabel?: string;
  }[] = [];

  switch (currentPage) {
    case "dashboard":
      if (!hasSchedule) {
        nextSteps.push({
          title: "Create Your First Schedule",
          description:
            "Add your supplements and set your wake time to get started",
          action: onNavigate ? () => onNavigate("presets") : undefined,
          actionLabel: "Add Supplements",
        });
      } else if (!hasPlan) {
        nextSteps.push({
          title: "Generate Today's Plan",
          description: "Create your personalized daily schedule",
          actionLabel: "Wake Up",
        });
      } else {
        nextSteps.push({
          title: "Track Your Progress",
          description: "Mark doses as taken to maintain your streak",
        });
      }
      break;

    case "presets":
      if (supplementCount === 0) {
        nextSteps.push({
          title: "Add Your First Supplement",
          description: "Search for vitamins, minerals, or supplements you take",
          actionLabel: "Browse Supplements",
        });
      } else if (supplementCount < 3) {
        nextSteps.push({
          title: "Add More Supplements",
          description: "Add all your daily supplements for optimal scheduling",
        });
        nextSteps.push({
          title: "Create a Schedule",
          description: "Build your daily routine once you've added supplements",
          action: onNavigate ? () => onNavigate("schedule") : undefined,
          actionLabel: "Build Schedule",
        });
      } else {
        nextSteps.push({
          title: "Create a Schedule",
          description: "Build your daily routine with your supplements",
          action: onNavigate ? () => onNavigate("schedule") : undefined,
          actionLabel: "Build Schedule",
        });
        nextSteps.push({
          title: "Organize with Groups",
          description:
            "Group related supplements together for easier management",
          action: onNavigate ? () => onNavigate("groups") : undefined,
          actionLabel: "Create Groups",
        });
      }
      break;

    case "schedule":
      if (!hasSchedule) {
        nextSteps.push({
          title: "Set Your Wake Time",
          description:
            "Your schedule will be optimized around when you wake up",
        });
        nextSteps.push({
          title: "Configure Gap Rules",
          description: "Prevent supplement interactions with automatic spacing",
        });
      } else {
        nextSteps.push({
          title: "Test Your Schedule",
          description: "Generate a plan to see how your timing looks",
          action: onNavigate ? () => onNavigate("dashboard") : undefined,
          actionLabel: "Go to Dashboard",
        });
      }
      break;

    case "groups":
      nextSteps.push({
        title: "Create Supplement Groups",
        description:
          'Group supplements that you take together (e.g., "Morning Stack")',
      });
      nextSteps.push({
        title: "Use in Schedule",
        description: "Add groups to your schedule for synchronized timing",
        action: onNavigate ? () => onNavigate("schedule") : undefined,
        actionLabel: "Build Schedule",
      });
      break;

    case "settings":
      nextSteps.push({
        title: "Enable Notifications",
        description: "Get reminders when it's time to take your supplements",
      });
      nextSteps.push({
        title: "Backup Your Data",
        description: "Your supplements and schedules are automatically saved",
      });
      break;

    case "history":
      nextSteps.push({
        title: "Improve Your Streak",
        description: "Consistent daily tracking helps build healthy habits",
        action: onNavigate ? () => onNavigate("dashboard") : undefined,
        actionLabel: "Today's Plan",
      });
      break;
  }

  return nextSteps;
};
